// Angular
import { EnvironmentProviders, provideZoneChangeDetection, provideZonelessChangeDetection } from '@angular/core';

// Local
import { EnvConfig, ENVIRONMENT } from '@app/types/environments';

/**
 * Bootstrap change-detection mode selected from the compile-time environment soak flag.
 */
export enum APP_CHANGE_DETECTION_MODE {
  ZONE = 'zone',
  ZONELESS = 'zoneless',
}

/**
 * Environments allowed to opt into {@link provideZonelessChangeDetection} for soak.
 * Production is never included — it always stays Zone until Phase 4.
 */
export const ZONELESS_SOAK_ENVIRONMENTS: readonly ENVIRONMENT[] = [
  ENVIRONMENT.LOCAL,
  ENVIRONMENT.DEV,
  ENVIRONMENT.UAT,
];

type ChangeDetectionBootstrapConfig = Pick<EnvConfig, 'Environment'> & {
  useZonelessChangeDetection?: boolean;
};

/**
 * Resolves Zone vs zoneless bootstrap from the compile-time env flag.
 *
 * Zoneless is used only when `useZonelessChangeDetection` is `true` **and** the
 * environment is local, dev (`ng serve`), or UAT. Production always returns Zone.
 */
export function resolveAppChangeDetectionMode(
  env: ChangeDetectionBootstrapConfig,
): APP_CHANGE_DETECTION_MODE {
  const envName = env.Environment?.Env;
  const soakAllowed = ZONELESS_SOAK_ENVIRONMENTS.some(allowed => allowed === envName);

  if (!soakAllowed || env.useZonelessChangeDetection !== true) {
    return APP_CHANGE_DETECTION_MODE.ZONE;
  }

  return APP_CHANGE_DETECTION_MODE.ZONELESS;
}

/**
 * Provides either {@link provideZonelessChangeDetection} or
 * {@link provideZoneChangeDetection} for `bootstrapApplication`.
 * `zone.js` stays in polyfills either way; this only switches CD scheduling.
 */
export function provideAppChangeDetection(
  env: ChangeDetectionBootstrapConfig,
): EnvironmentProviders {
  if (resolveAppChangeDetectionMode(env) === APP_CHANGE_DETECTION_MODE.ZONELESS) {
    return provideZonelessChangeDetection();
  }

  return provideZoneChangeDetection({ eventCoalescing: true });
}
